import { useRef } from 'react';

import { useEnergyContext } from '@features/Energy/Provider';
import {
  BundleBasketFragment,
  EnergyBasketFragment,
  EnergyQuoteType,
} from '@generated/gql/graphql';
import { useSyncedAction } from '@hooks/useSyncedAction';
import { RequestID } from '@hooks/useSyncedRequest';
import { BooleanSelection } from '@src/types/index';

import { EnergyFormValues } from '../types';
import { extractBasketFromMutationResult } from './extractBasketFromMutationResult';
import { useEvInterestUpdate } from './useEvInterestUpdate';
import { useTariffSelection } from './useTariffSelection';

const getInitialValues = (
  basket: Nullable<BundleBasketFragment>
): EnergyFormValues => {
  if (!basket) {
    return {
      evInterested: BooleanSelection.No,
      tariffType: '',
    };
  }

  const evInterest =
    basket.energy?.details?.electricVehicleTariffExpressionOfInterest;

  return {
    evInterested: evInterest ? BooleanSelection.Yes : BooleanSelection.No,
    tariffType: basket.energy?.selectedQuote?.quoteType || '',
  };
};

const useEnergyForm = () => {
  const formRef = useRef<HTMLFormElement>(null);

  const { basketRaw } = useEnergyContext();

  const {
    submitEvInterest,
    loading: isSubmittingEvInterest,
    error: evError,
  } = useEvInterestUpdate();

  const {
    handleSelect,
    loading: isSelectingTariff,
    error: selectError,
  } = useTariffSelection();

  const { updateActionLoading, syncedLoading } = useSyncedAction(
    RequestID.ENERGY_QUOTE_SELECT
  );

  const basket = basketRaw as BundleBasketFragment;
  const initialValues = getInitialValues(basket);

  const submitForm = async (
    formValues: EnergyFormValues,
    showEVBanner: boolean,
    energy: EnergyBasketFragment,
    onSuccess: (basket: Nullable<BundleBasketFragment>) => void
  ) => {
    const { evInterested, tariffType } = formValues;
    const shouldUpdateEVInterest =
      showEVBanner && evInterested !== initialValues.evInterested;
    const shouldSelectTariff =
      tariffType && tariffType !== initialValues.tariffType;

    if (!shouldUpdateEVInterest && !shouldSelectTariff) {
      onSuccess(basket);
      return;
    }

    let latestBasket: Nullable<BundleBasketFragment> = null;

    if (shouldSelectTariff) {
      const tariffResult = await updateActionLoading(
        handleSelect(tariffType as EnergyQuoteType)
      );
      latestBasket = extractBasketFromMutationResult([tariffResult]);
    }

    if (shouldUpdateEVInterest) {
      const targetEnergy = latestBasket?.energy ?? energy;
      const evResult = await updateActionLoading(
        submitEvInterest(evInterested === BooleanSelection.Yes, targetEnergy)
      );
      latestBasket = extractBasketFromMutationResult([evResult]);
    }

    onSuccess(latestBasket ?? basket);
  };

  return {
    formRef,
    initialValues,
    submitForm,
    isSubmitting: isSubmittingEvInterest || isSelectingTariff || syncedLoading,
    hasSubmitError: Boolean(evError || selectError),
  };
};

export { useEnergyForm };
